import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmOwnerClient, FirearmOwnerDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmOwnerClient = () => new FirearmOwnerClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns (all owners)
export const useFirearmOwners = () =>
  useQuery({
    queryKey: ['firearmOwners'],
    queryFn: async () => {
      const response = await getFirearmOwnerClient().searchFirearmOwners({
        filters: { isActive: true },
        page: 1,
        pageSize: 999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmOwnerSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmOwnerClient().searchFirearmOwners(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmOwnerById = (id: string) =>
  useQuery({
    queryKey: ['firearmOwner', id],
    queryFn: async () => {
      const response = await getFirearmOwnerClient().getFirearmOwnerById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmOwnerDTO) => {
      await getFirearmOwnerClient().createFirearmOwner(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmOwners'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmOwnerDTO }) => {
      await getFirearmOwnerClient().updateFirearmOwner(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmOwners'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmOwnerClient().deleteFirearmOwner(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmOwners'] });
    },
  });
};